import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  orderBy
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Database from '../types/Database'

// --- Error types ---

export type PaymentErrorCode =
  | 'PAYMENT_EXPIRED'
  | 'UNAUTHORIZED'
  | 'NETWORK_ERROR'
  | 'INVALID_STATE'
  | 'DIVE_CANCELLED'

export class PaymentError extends Error {
  constructor(public code: PaymentErrorCode, message: string) {
    super(message)
    this.name = 'PaymentError'
  }
}

// --- Service functions ---

export async function requestPaymentIntent(
  joinRequestId: string
): Promise<Database.CreatePaymentIntentResponse> {
  const functions = getFunctions()
  const createPaymentIntent = httpsCallable<
    Database.CreatePaymentIntentRequest,
    Database.CreatePaymentIntentResponse
  >(functions, 'createPaymentIntent')

  try {
    const result = await createPaymentIntent({ joinRequestId })
    return result.data
  } catch (error: any) {
    if (error?.code === 'functions/unauthenticated')
      throw new PaymentError('UNAUTHORIZED', 'Authentication required')
    if (error?.code === 'functions/permission-denied')
      throw new PaymentError('UNAUTHORIZED', 'Not authorized')
    if (
      error?.code === 'functions/deadline-exceeded' ||
      error?.message?.includes('expired')
    )
      throw new PaymentError('PAYMENT_EXPIRED', 'Payment window expired')
    if (error?.code === 'functions/failed-precondition') {
      const message = error?.message || 'Invalid payment state'
      if (message.toLowerCase().includes('cancelled'))
        throw new PaymentError('DIVE_CANCELLED', message)
      throw new PaymentError('INVALID_STATE', message)
    }
    throw new PaymentError('NETWORK_ERROR', 'Failed to create payment intent')
  }
}

export async function getTransactionsByHost(
  hostId: string
): Promise<Database.Transaction[]> {
  const db = getFirestore()
  const transactionsRef = collection(db, 'transactions')
  const q = query(
    transactionsRef,
    where('hostId', '==', hostId),
    orderBy('createdAt', 'desc')
  )
  const snap = await getDocs(q)
  return snap.docs.map(
    doc => ({ id: doc.id, ...doc.data() } as Database.Transaction)
  )
}

export async function getTransactionByJoinRequest(
  joinRequestId: string,
  diveId: string
): Promise<Database.Transaction | null> {
  const db = getFirestore()
  const transactionsRef = collection(db, 'transactions')
  const q = query(transactionsRef, where('diveId', '==', diveId))
  const snap = await getDocs(q)
  if (snap.empty) return null
  return { id: snap.docs[0].id, ...snap.docs[0].data() } as Database.Transaction
}
